// src/pages/admin/coupons/AdminCouponDetail.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getCoupon,
  updateCoupon,
  toggleCouponStatus,
  deleteCoupon,
  generateCode,
} from '../../../api/adminCoupons';
import { useTranslation } from 'react-i18next';
import AdminCouponUsersDialog from './AdminCouponUsersDialog';

// 日本語: バリデーション用の正規表現（保存はサーバ側で小文字に統一）
const CODE_RE = /^[A-Z0-9-]{4,32}$/;       // 表示入力は大文字。送信前に toLowerCase
const LINK_CODE_RE = /^[a-zA-Z0-9]{4,24}$/; // 英数のみ

type DiscountType = '' | 'percent' | 'amount' | 'fixed_price';
type CommissionType = '' | 'percent' | 'amount';

const AdminCouponDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const numericId = id ? Number(id) : NaN;

  // ユーティリティ: UTC文字列をJST datetime-local文字列に変換
  const utcToJstLocal = (utcStr: string | null | undefined): string => {
    if (!utcStr) return '';
    try {
      const date = new Date(utcStr);
      // JSTに変換 (+9時間)
      const jstDate = new Date(date.getTime() + 9 * 60 * 60 * 1000);
      // datetime-local形式 (YYYY-MM-DDTHH:mm)
      const year = jstDate.getUTCFullYear();
      const month = String(jstDate.getUTCMonth() + 1).padStart(2, '0');
      const day = String(jstDate.getUTCDate()).padStart(2, '0');
      const hours = String(jstDate.getUTCHours()).padStart(2, '0');
      const minutes = String(jstDate.getUTCMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return '';
    }
  };

  // ユーティリティ: JST datetime-local文字列をUTC ISO文字列に変換
  const jstLocalToUtc = (jstLocalStr: string): string | null => {
    if (!jstLocalStr) return null;
    try {
      // datetime-localの値は「YYYY-MM-DDTHH:mm」形式
      // これをJST時刻として解釈し、UTC ISOに変換
      const [datePart, timePart] = jstLocalStr.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hours, minutes] = timePart.split(':').map(Number);

      // JSTとしてDateオブジェクトを作成 (UTC+9として扱う)
      const jstDate = new Date(Date.UTC(year, month - 1, day, hours - 9, minutes));
      return jstDate.toISOString();
    } catch {
      return null;
    }
  };
  const navigate = useNavigate();
  const { t } = useTranslation();

  // —— 権限ガード —— 
  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    if (!token) navigate('/login/admin');
    else if (role !== 'admin') {
      alert(t('admin.common_access_denied') || 'アクセス権限がありません');
      navigate('/');
    }
  }, [navigate, t]);

  // —— 状態 —— 
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [code, setCode] = useState('');               // 画面は大文字入力/表示
  const [linkCode, setLinkCode] = useState('');
  const [isActive, setIsActive] = useState<boolean>(true);

  const [discountType, setDiscountType] = useState<DiscountType>('');
  const [discountValue, setDiscountValue] = useState<number | ''>('');

  const [commissionType, setCommissionType] = useState<CommissionType>('');
  const [commissionValue, setCommissionValue] = useState<number | ''>('');

  // 期間
  const [rangeMode, setRangeMode] = useState<'unlimited' | 'custom'>('unlimited');
  const [startsAt, setStartsAt] = useState<string>(''); // datetime-local
  const [endsAt, setEndsAt] = useState<string>('');

  // 利用回数制限
  const [maxTotal, setMaxTotal] = useState<number | ''>('');
  const [maxPerUser, setMaxPerUser] = useState<number | ''>('');

  // 備考
  const [note, setNote] = useState('');

  // ユーザーダイアログ
  const [showUsersDialog, setShowUsersDialog] = useState(false);

  // 日本語: 公開URL作成（link_code があれば）
  const publicBase = process.env.REACT_APP_PUBLIC_BASE_URL || window.location.origin;
  const publicUrl = linkCode ? `${publicBase.replace(/\/+$/, '')}/c/${linkCode}` : '';

  // —— 初期ロード —— 
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        setLoading(true);
        const data = await getCoupon(numericId);
        if (cancelled) return;

        setName(data.name || '');
        setCode(String(data.code || '').toUpperCase());
        setLinkCode(data.link_code || '');
        setIsActive(!!data.is_active);

        setDiscountType((data.discount_type || '') as DiscountType);
        setDiscountValue(
          data.discount_value === null || data.discount_value === undefined ? '' : Number(data.discount_value)
        );

        setCommissionType((data.commission_type || '') as CommissionType);
        setCommissionValue(
          data.commission_value === null || data.commission_value === undefined ? '' : Number(data.commission_value)
        );

        // 利用回数
        setMaxTotal(
          data.max_redemptions_total === null || data.max_redemptions_total === undefined ? '' : Number(data.max_redemptions_total)
        );
        setMaxPerUser(
          data.max_redemptions_per_user === null || data.max_redemptions_per_user === undefined ? '' : Number(data.max_redemptions_per_user)
        );

        // 備考
        setNote(data.note || '');

        // 期間 - UTCからJSTに変換して表示
        const s = utcToJstLocal(data.starts_at);
        const e = utcToJstLocal(data.ends_at);
        if (!s && !e) setRangeMode('unlimited');
        else setRangeMode('custom');
        setStartsAt(s);
        setEndsAt(e);
      } catch (e: any) {
        setErr(e?.response?.data?.detail || t('admin.coupons.errors.load_failed'));
      } finally {
        setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [numericId]);

  // —— バリデーション —— 
  const discountError = useMemo(() => {
    if (!discountType) return null;
    if (discountValue === '') return t('admin.coupons.errors.discount_required') as string;
    const v = Number(discountValue);
    if (Number.isNaN(v)) return t('admin.coupons.errors.number_required') as string;
    if (discountType === 'percent' && (v < 0 || v > 100)) return t('admin.coupons.errors.percent_range') as string;
    if ((discountType === 'amount' || discountType === 'fixed_price') && v < 0)
      return t('admin.coupons.errors.non_negative') as string;
    return null;
  }, [discountType, discountValue, t]);

  const commissionError = useMemo(() => {
    if (!commissionType) return null;
    if (commissionValue === '') return t('admin.coupons.errors.commission_required') as string;
    const v = Number(commissionValue);
    if (Number.isNaN(v)) return t('admin.coupons.errors.number_required') as string;
    if (commissionType === 'percent' && (v < 0 || v > 100)) return t('admin.coupons.errors.percent_range') as string;
    if (commissionType === 'amount' && v < 0) return t('admin.coupons.errors.non_negative') as string;
    return null;
  }, [commissionType, commissionValue, t]);

  const codeError = useMemo(() => {
    if (!code.trim()) return t('admin.coupons.errors.number_required') as string;
    if (!CODE_RE.test(code.trim().toUpperCase())) return t('admin.coupons.errors.code_format') as string;
    return null;
  }, [code, t]);

  const linkCodeError = useMemo(() => {
    if (!linkCode) return null;
    if (!LINK_CODE_RE.test(linkCode)) return t('admin.coupons.errors.link_code_format') as string;
    return null;
  }, [linkCode, t]);

  // 期間チェック
  const periodError = useMemo(() => {
    if (rangeMode === 'unlimited') return null;
    if (!startsAt && !endsAt) return t('admin.coupons.errors.period_required') as string;
    if (startsAt && endsAt && new Date(startsAt) > new Date(endsAt)) {
      return t('admin.coupons.errors.period_invalid') as string;
    }
    return null;
  }, [rangeMode, startsAt, endsAt, t]);

  const disableSave =
    saving || !!discountError || !!commissionError || !!codeError || !!linkCodeError || !!periodError || !name.trim();

  // —— 操作 —— 
  const handleSave = async () => {
    setErr(null);

    const payload: any = {
      name: name.trim(),
      code: code.trim().toLowerCase(),
      link_code: linkCode ? linkCode.trim().toLowerCase() : null,
      discount_type: discountType || null,
      discount_value: discountValue === '' ? null : Number(discountValue),
      commission_type: commissionType || null,
      commission_value: commissionValue === '' ? null : Number(commissionValue),
      max_redemptions_total: maxTotal === '' ? null : Number(maxTotal),
      max_redemptions_per_user: maxPerUser === '' ? null : Number(maxPerUser),
      note: note.trim() || null,
    };

    if (rangeMode === 'unlimited') {
      payload.starts_at = null;
      payload.ends_at = null;
    } else {
      // JST datetime-localをUTC ISOに変換
      payload.starts_at = jstLocalToUtc(startsAt);
      payload.ends_at = jstLocalToUtc(endsAt);
    }

    try {
      setSaving(true);
      await updateCoupon(numericId, payload);
      alert(t('admin.common.saved') || '保存しました');
      // 反映を即時表示
      const refreshed = await getCoupon(numericId);
      setName(refreshed.name || '');
      setCode(String(refreshed.code || '').toUpperCase());
      setLinkCode(refreshed.link_code || '');
      setIsActive(!!refreshed.is_active);
      setDiscountType((refreshed.discount_type || '') as DiscountType);
      setDiscountValue(
        refreshed.discount_value === null || refreshed.discount_value === undefined
          ? ''
          : Number(refreshed.discount_value)
      );
      setCommissionType((refreshed.commission_type || '') as CommissionType);
      setCommissionValue(
        refreshed.commission_value === null || refreshed.commission_value === undefined
          ? ''
          : Number(refreshed.commission_value)
      );
      setMaxTotal(
        refreshed.max_redemptions_total === null || refreshed.max_redemptions_total === undefined
          ? ''
          : Number(refreshed.max_redemptions_total)
      );
      setMaxPerUser(
        refreshed.max_redemptions_per_user === null || refreshed.max_redemptions_per_user === undefined
          ? ''
          : Number(refreshed.max_redemptions_per_user)
      );
      setNote(refreshed.note || '');
      // 期間 - UTCからJSTに変換して表示
      const s = utcToJstLocal(refreshed.starts_at);
      const e = utcToJstLocal(refreshed.ends_at);
      setRangeMode(!s && !e ? 'unlimited' : 'custom');
      setStartsAt(s);
      setEndsAt(e);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || (t('admin.coupons.update_failed') as string));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async () => {
    try {
      const next = !isActive;
      await toggleCouponStatus(numericId, next);
      setIsActive(next);
    } catch (e: any) {
      alert(e?.response?.data?.detail || '切り替えに失敗しました');
    }
  };

  const handleDelete = async () => {
    const ok = window.confirm(t('admin.common.delete_confirm') || '本当に削除しますか？この操作は取り消せません。');
    if (!ok) return;
    try {
      await deleteCoupon(numericId);
      alert(t('admin.common.deleted') || '削除しました');
      navigate('/admin/coupons');
    } catch (e: any) {
      alert(e?.response?.data?.detail || '削除に失敗しました');
    }
  };

  const handleGenerate = async () => {
    try {
      const r = await generateCode();
      if (!r?.code) throw new Error('invalid_response');
      setCode(String(r.code).toUpperCase());
    } catch {
      alert(t('admin.coupons.fallback_generated') || 'サーバーで生成に失敗したため、ローカルで仮コードを生成してください。');
    }
  };

  const copyToClipboard = async (txt: string) => {
    try {
      await navigator.clipboard.writeText(txt);
      alert(t('admin.coupons.toasts.copied'));
    } catch {
      alert(t('admin.coupons.errors.copy_failed'));
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">{t('admin.common.loading')}</div>;
  }

  return (
    <div className="w-full min-h-[calc(100vh-64px)] flex items-start justify-center p-6">
      <div className="w-full max-w-2xl">
        {/* ヘッダー */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">{t('admin.coupons.detail_title') || 'クーポン詳細'}</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handleToggle}
              className={`px-3 py-2 rounded text-white ${isActive ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-gray-500 hover:bg-gray-600'}`}
              title={isActive ? t('admin.coupons.status.active') : t('admin.coupons.status.inactive')}
            >
              {isActive ? t('admin.coupons.actions.deactivate') || '無効化' : t('admin.coupons.actions.activate') || '有効化'}
            </button>
            <button
              onClick={handleDelete}
              className="px-3 py-2 rounded bg-red-600 text-white hover:bg-red-700"
            >
              {t('admin.common.delete') || '削除'}
            </button>
            <button
              onClick={() => setShowUsersDialog(true)}
              className="px-3 py-2 rounded bg-coral text-white hover:opacity-90"
            >
              {t('admin.coupons.view_users') || 'ユーザーを表示'}
            </button>
          </div>
        </div>

        {/* 本体フォーム */}
        <form
          className="space-y-5 bg-white rounded-2xl shadow p-6"
          onSubmit={(e) => { e.preventDefault(); if (!disableSave) handleSave(); }}
        >
          {/* 名称 */}
          <div>
            <label className="block text-sm font-medium mb-1">{t('admin.coupons.fields.name')}</label>
            <input
              className="w-full border rounded px-3 py-2"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('admin.coupons.placeholders.name') || ''}
              required
            />
          </div>

          {/* クーポンコード */}
          <div>
            <label className="block text-sm font-medium mb-1">{t('admin.coupons.fields.code')}</label>
            <div className="flex gap-2">
              <input
                className={`flex-1 border rounded px-3 py-2 uppercase ${codeError ? 'border-red-500' : ''}`}
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder={t('admin.coupons.placeholders.code') || ''}
                inputMode="text"
                pattern="[A-Z0-9-]{4,32}"
                title="A–Z・0–9・-（4〜32文字）"
                required
              />
              <button
                type="button"
                onClick={handleGenerate}
                className="px-3 py-2 rounded border hover:bg-gray-50"
              >
                {t('admin.coupons.actions.generate')}
              </button>
              <button
                type="button"
                onClick={() => copyToClipboard(code)}
                className="px-3 py-2 rounded border hover:bg-gray-50"
              >
                {t('admin.coupons.actions.copy_code')}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">{t('admin.coupons.hints.code_case')}</p>
            {codeError && <p className="text-xs text-red-600 mt-1">{codeError}</p>}
          </div>

          {/* リンクコード + 公開URL */}
          <div>
            <label className="block text-sm font-medium mb-1">{t('admin.coupons.fields.link_code')}</label>
            <div className="flex gap-2">
              <input
                className={`flex-1 border rounded px-3 py-2 ${linkCodeError ? 'border-red-500' : ''}`}
                value={linkCode}
                onChange={(e) => setLinkCode(e.target.value)}
                placeholder={t('admin.coupons.placeholders.link_code') || ''}
                inputMode="text"
                pattern="[a-zA-Z0-9]{4,24}"
                title="a–z・A–Z・0–9（4〜24文字）"
              />
              {linkCode && (
                <button
                  type="button"
                  onClick={() => copyToClipboard(linkCode)}
                  className="px-3 py-2 rounded border hover:bg-gray-50"
                >
                  {t('admin.coupons.actions.copy_link_code')}
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">{t('admin.coupons.hints.link_auto')}</p>

            {publicUrl && (
              <div className="mt-2 text-sm">
                <span className="text-gray-500 mr-2">{t('admin.coupons.labels.public_url') || 'URL'}</span>
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-coral hover:underline break-all"
                >
                  {publicUrl}
                </a>
                <button
                  type="button"
                  onClick={() => copyToClipboard(publicUrl)}
                  className="ml-2 text-xs px-2 py-0.5 border rounded hover:bg-gray-50"
                >
                  {t('admin.coupons.actions.copy')}
                </button>
              </div>
            )}

            {linkCodeError && <p className="text-xs text-red-600 mt-1">{linkCodeError}</p>}
          </div>

          {/* 割引 */}
          <div>
            <label className="block text-sm font-medium mb-2">{t('admin.coupons.sections.discount')}</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">{t('admin.coupons.fields.discount_type')}</label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={discountType}
                  onChange={(e) => setDiscountType(e.target.value as DiscountType)}
                >
                  <option value="">{t('admin.coupons.options.none')}</option>
                  <option value="percent">{t('admin.coupons.options.percent')}</option>
                  <option value="amount">{t('admin.coupons.options.amount')}</option>
                  <option value="fixed_price">{t('admin.coupons.options.fixed_price')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('admin.coupons.fields.discount_value')}</label>
                <div className="relative">
                  <input
                    type="number"
                    className="w-full border rounded px-3 py-2 pr-10"
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value === '' ? '' : Number(e.target.value))}
                    min={0}
                    step={discountType === 'percent' ? 1 : 100}
                    placeholder={
                      discountType === 'percent'
                        ? (t('admin.coupons.placeholders.discount_percent_example') as string) || '10'
                        : discountType === 'amount'
                          ? '2000'
                          : discountType === 'fixed_price'
                            ? '9800'
                            : ''
                    }
                    disabled={!discountType}
                  />
                  {discountType === 'percent' && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                  )}
                </div>
                {discountError && <p className="text-xs text-red-600 mt-1">{discountError}</p>}
              </div>
            </div>
          </div>

          {/* 返金/返佣 */}
          <div>
            <label className="block text-sm font-medium mb-2">{t('admin.coupons.sections.commission')}</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">{t('admin.coupons.fields.commission_type')}</label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={commissionType}
                  onChange={(e) => setCommissionType(e.target.value as CommissionType)}
                >
                  <option value="">{t('admin.coupons.options.none')}</option>
                  <option value="percent">{t('admin.coupons.options.percent')}</option>
                  <option value="amount">{t('admin.coupons.options.amount')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('admin.coupons.fields.commission_value')}</label>
                <div className="relative">
                  <input
                    type="number"
                    className="w-full border rounded px-3 py-2 pr-10"
                    value={commissionValue}
                    onChange={(e) => setCommissionValue(e.target.value === '' ? '' : Number(e.target.value))}
                    min={0}
                    step={commissionType === 'percent' ? 1 : 100}
                    placeholder={commissionType === 'percent' ? '5' : commissionType === 'amount' ? '500' : ''}
                    disabled={!commissionType}
                  />
                  {commissionType === 'percent' && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                  )}
                </div>
                {commissionError && <p className="text-xs text-red-600 mt-1">{commissionError}</p>}
              </div>
            </div>
          </div>

          {/* 利用回数制限 */}
          <div>
            <label className="block text-sm font-medium mb-2">
              {t('admin.coupons.sections.redemptions') || '利用回数制限'}
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('admin.coupons.fields.max_redemptions_total') || '全体での最大利用回数'}
                </label>
                <input
                  type="number"
                  className="w-full border rounded px-3 py-2"
                  value={maxTotal}
                  onChange={(e) => setMaxTotal(e.target.value === '' ? '' : Number(e.target.value))}
                  min={1}
                  placeholder={t('admin.coupons.placeholders.max_total') || '空欄で制限なし'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('admin.coupons.fields.max_redemptions_per_user') || '1ユーザーあたりの最大利用回数'}
                </label>
                <input
                  type="number"
                  className="w-full border rounded px-3 py-2"
                  value={maxPerUser}
                  onChange={(e) => setMaxPerUser(e.target.value === '' ? '' : Number(e.target.value))}
                  min={1}
                  placeholder={t('admin.coupons.placeholders.max_per_user') || '空欄で制限なし'}
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {t('admin.coupons.hints.redemptions') ||
                '空欄の場合は上限なしとして扱われます。'}
            </p>
          </div>

          {/* 備考 */}
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('admin.coupons.fields.note') || '備考'}
            </label>
            <textarea
              className="w-full border rounded px-3 py-2 min-h-[60px]"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('admin.coupons.placeholders.note') || '例：〇〇さん用クーポン'}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('admin.coupons.hints.note') ||
                'クーポン一覧の名称の下に表示されます。クーポンの用途を区別する際にご利用ください。'}
            </p>
          </div>

          {/* 期間 */}
          <div>
            <label className="block text-sm font-medium mb-2">{t('admin.coupons.sections.period') || '期間'}</label>

            <div className="flex items-center gap-4 mb-2">
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="range_mode"
                  checked={rangeMode === 'unlimited'}
                  onChange={() => setRangeMode('unlimited')}
                />
                <span>{t('admin.coupons.period.unlimited') || '無期限'}</span>
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="range_mode"
                  checked={rangeMode === 'custom'}
                  onChange={() => setRangeMode('custom')}
                />
                <span>{t('admin.coupons.period.custom') || 'カスタム'}</span>
              </label>
            </div>

            {rangeMode === 'custom' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">{t('admin.coupons.period.starts_at') || '開始'}</label>
                  <input
                    type="datetime-local"
                    className="w-full border rounded px-3 py-2"
                    value={startsAt}
                    onChange={(e) => setStartsAt(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t('admin.coupons.period.ends_at') || '終了'}</label>
                  <input
                    type="datetime-local"
                    className="w-full border rounded px-3 py-2"
                    value={endsAt}
                    onChange={(e) => setEndsAt(e.target.value)}
                  />
                </div>
              </div>
            )}
            {periodError && <p className="text-xs text-red-600 mt-1">{periodError}</p>}
          </div>

          {/* エラー */}
          {err && <div className="text-sm text-red-600">{err}</div>}

          {/* アクション */}
          <div className="flex items-center justify-center gap-3 pt-2">
            <button
              type="submit"
              disabled={disableSave}
              className="px-5 py-2.5 rounded-lg bg-coral text-white hover:opacity-90 disabled:opacity-60"
            >
              {saving ? t('admin.common.saving') : t('admin.common.save')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/coupons')}
              className="px-5 py-2.5 rounded-lg border hover:bg-gray-50"
            >
              {t('admin.common.back') || '戻る'}
            </button>
          </div>
        </form>

        {/* ユーザーダイアログ */}
        <AdminCouponUsersDialog
          couponId={numericId}
          couponName={name}
          open={showUsersDialog}
          onClose={() => setShowUsersDialog(false)}
        />
      </div>
    </div>
  );
};

export default AdminCouponDetail;
